import React, { useEffect, useMemo, useRef, useState } from "react";
import ConversationIcon from "../../../Assets/svgs/ConversationIcon";
import ModernEndCallIcon from "../../../Assets/svgs/ModernEndCallIcon";
import SmallLogoIcon from "../../../Assets/svgs/SmallLogoIcon";
import SwitchManualIcon from "../../../Assets/svgs/SwitchManualIcon";
import TalkMicIcon from "../../../Assets/svgs/TalkMicIcon";
import { Loader } from "../../../Common/Loader";
import { ErrorModel } from "../../../Model/ErrorModel";
import { SuccessModel } from "../../../Model/SuccessModel";
import { vapi } from "../../Talk/Talk";
import ConversationSidebar from "./ConversationSidebar";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { getAssistantForBuilder } from "../../../Redux/Slice/Assistant/GetAssistantForBuilderSlice";
import { emailCallWebhook } from "../../../Redux/Slice/Talk/EmailCallWebhookSlice";
import { getAssistantSettingForBuilder } from "../../../Redux/Slice/Assistant/GetAssistantSettingForBuilderSlice";
import { getVoiceFromStructuredData } from "../../../Common/Constants";
import { ConfirmModel } from "../../../Model/ConfirmModel";
import { CreateAssistantPopup } from "../CreateAssistantPopUp";
import { debounce } from "../../../Common/debounce";
import MessageLoadingModal from "../../../Model/MessageLoadingModal";
import { setUnsavedChanges } from "../../../Redux/Slice/Common/SelectedTabSlice";

const AddAssistantBuilder = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [assistantMetaData, setAssistantMetaData] = useState(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializingAssistant, setIsInitializingAssistant] = useState(false);
  const [showConversation, setShowConversation] = useState(false);
  const [isCallActive, setIsCallActive] = useState(false);
  const [createAssistantPopUp, setCreateAssistantPopUp] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [conversation, setConversation] = useState([]);
  const [successModal, setSuccessModal] = useState({
    visible: false,
    message: "",
  });
  const [confirmModal, setConfirmModal] = useState({
    visible: false,
    title: "",
    message: "",
    type: "",
  });
  const chatEndRef = useRef(null);
  const customerId = location.pathname.includes("/add-assistant-builder/")
    ? location.pathname.split("/")[2]
    : "";
  const last4Conversation = useMemo(() => {
    return conversation
      .filter((messages) => messages.role != "user")
      ?.slice(-4);
  }, [conversation]);

  const isAssistantConfigured = useMemo(() => {
    return conversation.some((item) =>
      item?.content?.toLocaleLowerCase()?.includes("configured")
    );
  }, [JSON.stringify(conversation)]);

  const [errorModal, setErrorModal] = useState({ visible: false, message: "" });

  const showSuccessMessage = (message, callback) => {
    setSuccessModal({
      visible: true,
      message: message ?? "Something went wrong, please try again later!",
    });

    setTimeout(() => {
      setSuccessModal({ visible: false, message: "" });
      if (typeof callback === "function") {
        callback();
      }
    }, 3000);
  };

  const showErrorMessage = (message, callback) => {
    setErrorModal({
      visible: true,
      message: message ?? "Something went wrong, please try again later!",
    });

    setTimeout(() => {
      setErrorModal({ visible: false, message: "" });
      if (typeof callback === "function") {
        callback();
      }
    }, 2000);
  };

  const scrollToEndChat = () => {
    try {
      chatEndRef?.current &&
        chatEndRef?.current?.scrollIntoView({ behavior: "smooth" });
    } catch (error) {
      console.log(error);
    }
  };

  const startCall = async () => {
    setIsLoading(true);
    try {
      const response = await dispatch(getAssistantForBuilder());
      if (response?.payload?.success) {
        const metaData = response?.payload?.data;
        const call = await vapi.start(metaData?.vapiAssistantId);
        if (call) {
          const payload = {
            assistantId: metaData?.vapiAssistantId,
            token: metaData?.token,
            payload: call,
          };
          setAssistantMetaData({ ...metaData, call: call });
          dispatch(emailCallWebhook(payload));
          dispatch(setUnsavedChanges(true));
        }
      } else {
        showErrorMessage("Something went wrong, please try again.", () =>
          navigate(-1)
        );
      }
    } catch (error) {
      showErrorMessage("Something went wrong, please try again.", () =>
        navigate(-1)
      );
    } finally {
      setIsLoading(false);
    }
  };

  const getAssistantSettings = async () => {
    try {
      const response = await dispatch(
        getAssistantSettingForBuilder(assistantMetaData?.call?.id)
      );
      if (
        response?.payload?.success &&
        response?.payload?.data?.structuredData
      ) {
        const metaData = response?.payload?.data;
        const voiceId = getVoiceFromStructuredData(
          metaData?.structuredData?.voiceType,
          metaData?.structuredData?.accent,
          metaData?.structuredData?.GenderVoice
        );

        showSuccessMessage(
          "Congratulations! I’ve successfully gathered all your information, your can now review all the details and proceed with publishing your assistant.",
          () =>
            navigate(
              customerId ? `/add-assistant/${customerId}` : "/add-assistant",
              {
                state: {
                  name: metaData?.structuredData?.name,
                  from: "new",
                  planType: location?.state?.planType,
                  customerName: location?.state?.customerName,
                  details: {
                    name: metaData?.structuredData?.name,
                    firstMessage: metaData?.structuredData?.firstMessage,
                    hipaaEnabled: metaData?.structuredData?.advance
                      ? true
                      : false,
                    model: {
                      messages: [
                        {
                          role: "system",
                          content: metaData?.structuredData?.context,
                        },
                      ],
                    },
                    form: {
                      instructions: metaData?.structuredData?.form,
                    },
                    voice: {
                      voiceId: voiceId,
                    },
                  },
                },
              }
            )
        );

        return true;
      } else {
        return false;
      }
    } catch (error) {
      return false;
    }
  };

  const onAcceptConfirmModal = () => {
    const { type } = confirmModal;
    setConfirmModal({ visible: false, title: "", message: "", type: "" });

    if (type === "SWITCH_TO_MANUAL") {
      dispatch(setUnsavedChanges(true));
      vapi.stop();
      vapi.removeAllListeners();
      setCreateAssistantPopUp(true);
    } else if (type === "END_CALL") {
      dispatch(setUnsavedChanges(true));
      vapi.stop();
      navigate(-1);
    }
  };

  const onSwitchToManual = () => {
    setConfirmModal({
      visible: true,
      title: "Switch to manual mode",
      message:
        "Are you sure you want to switch to manual mode?\nAll your unsaved data will be lost.",
      type: "SWITCH_TO_MANUAL",
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      if (inputValue.trim()) {
        vapi.send({
          type: "add-message",
          message: {
            role: "user",
            content: inputValue,
          },
        });
        setInputValue("");
      }
    }
  };

  const onEndCall = async () => {
    setIsInitializingAssistant(true);
    let attempts = 0;
    dispatch(setUnsavedChanges(false));
    vapi.stop();
    try {
      const intervalId = setInterval(async () => {
        const isSuccess = await getAssistantSettings();
        if (isSuccess) {
          clearInterval(intervalId);
          setIsInitializingAssistant(false);
        } else {
          attempts++;
          if (attempts >= 6) {
            clearInterval(intervalId);
            setIsInitializingAssistant(false);
            showErrorMessage("Something went wrong, please try again.", () =>
              navigate(-1)
            );
          }
        }
      }, 5000);
    } catch (error) {
      setIsInitializingAssistant(false);
      showErrorMessage("Something went wrong, please try again.", () =>
        navigate(-1)
      );
    }
  };

  const onClickEndCall = () => {
    if (isAssistantConfigured) {
      onEndCall();
    } else {
      setConfirmModal({
        visible: true,
        title: "End Call",
        message:
          "Are you sure you want to end this call? All your unsaved data will be lost.",
        type: "END_CALL",
      });
    }
  };

  useEffect(() => {
    if (conversation?.length > 0) {
      scrollToEndChat();
    }
  }, [JSON.stringify(conversation)]);

  useEffect(() => {
    startCall();

    vapi.on("speech-start", () => setIsSpeaking(true));
    vapi.on("speech-end", () => setIsSpeaking(false));
    vapi.on("call-start", () => setIsCallActive(true));
    vapi.on("call-end", () => {
      setIsCallActive(false);
    });
    vapi.on("message", (message) => {
      if (
        message?.type == "conversation-update" &&
        Array.isArray(message?.conversation)
      ) {
        setConversation(message?.conversation?.slice(1));
      }
    });

    return () => {
      vapi.stop();
      vapi.removeAllListeners();
    };
  }, []);

  return (
    <div className="page__body">
      {isLoading && <Loader />}
      {createAssistantPopUp ? (
        <CreateAssistantPopup
          setPopUp={(val, isFromSubmit) => {
            if (!isFromSubmit) {
              setCreateAssistantPopUp(false);
              navigate(-1);
            }
          }}
        />
      ) : (
        ""
      )}
      {errorModal.visible && <ErrorModel message={errorModal.message} />}
      {successModal.visible ? (
        <SuccessModel message={successModal.message} />
      ) : isInitializingAssistant ? (
        <MessageLoadingModal
          message={
            "Initializing your assistant.\nThis process takes approximately 30 seconds. Thank you for your patience."
          }
        />
      ) : null}
      {confirmModal.visible ? (
        <ConfirmModel
          onAccept={onAcceptConfirmModal}
          onReject={() => {
            setConfirmModal({
              visible: false,
              title: "",
              message: "",
              type: "",
            });
          }}
          title={confirmModal?.title}
          message={confirmModal?.message}
        />
      ) : (
        ""
      )}
      <div
        className={`conversationWrap ${
          showConversation ? "conversationWrap--open" : ""
        }`}
      >
        <div className="chatwrap">
          <div className="chatwrap__body">
            <div className={`chatIcon ${isSpeaking ? "speaking" : ""}`}>
              <TalkMicIcon />
            </div>
            {last4Conversation.map((message, index) => (
              <p
                className={`para ${
                  last4Conversation?.length - 1 === index
                    ? "para--lg para--primary"
                    : last4Conversation?.length - 2 === index
                    ? "para--md para--white80"
                    : last4Conversation?.length - 3 === index
                    ? "para--white60"
                    : last4Conversation?.length - 4 === index
                    ? "para para--sm para--white40"
                    : ""
                }`}
              >
                “{message?.content}”
              </p>
            ))}
            {/* {last4Conversation.length &&
            last4Conversation[last4Conversation.length - 1]?.content?.includes(
              "?"
            ) ? (
              <div className="form__group">
                <input
                  id="name"
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="form__input form__input--sm"
                  placeholder="Type your answer here"
                />
              </div>
            ) : null} */}
          </div>
          <div className="chatwrap__footer">
            <div className="btn__group">
              <button
                className="btn btn__white"
                onClick={() => setShowConversation((p) => !p)}
              >
                <ConversationIcon />
                {showConversation ? "Hide" : "View"} Conversation
              </button>
              <button className="btn btn__white" onClick={onSwitchToManual}>
                <SwitchManualIcon />
                Switch to Manual
              </button>
              <button className="btn btn__danger" onClick={onClickEndCall}>
                <ModernEndCallIcon />
                End Chat
              </button>
            </div>
            <p className="para">
              <SmallLogoIcon />
              <a
                style={{ color: "#fff" }}
                href="http://www.voxapp.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                Powered By VoxApp
              </a>
            </p>
          </div>
        </div>
        {showConversation ? (
          <ConversationSidebar
            chatEndRef={chatEndRef}
            conversation={conversation}
            handleClose={() => setShowConversation(false)}
          />
        ) : null}
      </div>
    </div>
  );
};

export default AddAssistantBuilder;
